/**
 * @fileoverview Public/End-user API functions
 * 
 * Handles public API endpoints that don't require authentication including:
 * - Order placement
 * - Order status
 * - Menu access
 * - Table information
 * - Sessions
 * 
 * @module api/public
 */

import axios from 'axios';
import { BASE_URL, attachLanguageHeader, getCurrentUser, refreshAuthToken } from './client';

// Create a separate axios instance for public (unauthenticated) requests
const publicClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
attachLanguageHeader(publicClient);

// ============================================================================
// ORDERS (Public)
// ============================================================================

// The access token lives a few minutes and this public client never refreshes it: an expired one
// made the server take the till's tickets for a customer's (« Client · Table 1 » in the journal, no
// waiter on the order — The Diesel, 02/11/2026). Refresh it first when it is (nearly) expired.
const tokenExpiresAt = (token) => {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return (JSON.parse(atob(part)).exp || 0) * 1000;
  } catch {
    return 0;
  }
};
const freshStaffToken = async (token) => {
  if (!token || tokenExpiresAt(token) - Date.now() > 30_000) return token;
  try {
    return await refreshAuthToken();
  } catch {
    return token; // no refresh possible (customer, offline): the order still goes through
  }
};

/**
 * Place a new order
 * @param {Object} data - Order data
 * @returns {Promise} Created order
 */
export const placeOrder = async (data, { idempotencyKey } = {}) => {
  try {
    // The key lets a retried submission (dropped socket, offline replay) answer once server-side.
    // The POS sends the staff token: without it the server treats the ticket as a customer's
    // (waiting, no waiter, no discount) instead of sending it straight to the kitchen.
    const token = await freshStaffToken(getCurrentUser()?.access_token);
    const headers = {
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const response = await publicClient.post('/order/', data, { headers });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get order status
 * @param {string} orderId - Order ID
 * @returns {Promise} Order status
 */
export const getOrderStatus = async (orderId) => {
  try {
    const response = await publicClient.get(`/order/${orderId}/`);
    return response.data;
  } catch (error) {
    console.error('Order Status Error:', error);
    throw error;
  }
};

// ============================================================================
// MENU (Public)
// ============================================================================

/**
 * Get menu for end-user
 * @param {string} restaurantId - Restaurant ID
 * @returns {Promise} Menu data
 */
export const getMenuEndUser = async (restaurantId) => {
  try {
    const response = await publicClient.get(`/restaurant/${restaurantId}/menu/`);
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Get most bought menu items
 * @param {string} restaurantId - Restaurant ID
 * @param {number} limit - Maximum number of items to return (default: 10)
 * @returns {Promise} Most bought menu items
 */
export const getMostBoughtMenu = async (restaurantId, limit = 10) => {
  try {
    const response = await publicClient.get(
      `/restaurant/${restaurantId}/menu/most-bought/?limit=${limit}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching most bought menu:', error);
    throw error;
  }
};

/**
 * Get category for end-user
 * @param {string} restaurantId - Restaurant ID
 * @returns {Promise} Category data
 */
export const getCategory = async (restaurantId) => {
  try {
    const response = await publicClient.get(`/restaurant/${restaurantId}/category/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// ============================================================================
// ============================================================================
// SESSIONS (Public)
// ============================================================================

/**
 * Create a new session (for waiter access)
 * @param {Object} data - Session data
 * @returns {Promise} Created session
 */
export const postSession = async (data, { timeout } = {}) => {
  try {
    // `timeout`: a till never waits forever on « Ouverture de la table… » (QA 13/10 C4)
    const response = await publicClient.post('/sessions/', data, timeout ? { timeout } : undefined);
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
